import type { SavingsRegistryEntry } from '../registry'
import { readerErc4626 } from './erc4626'
import { readerErc4626Cooldown } from './erc4626Cooldown'
import { readerErc4626Idle } from './erc4626Idle'
import { readerErc4626WithdrawLimit } from './withdrawLimit'
import { readerBitfiBfbtc } from './bitfiBfbtc'
import { readerBitfiVault } from './bitfiVault'
import { readerBitwayVault } from './bitwayVault'
import { readerFrankencoinSavings } from './frankencoinSavings'
import { readerHyperbeatVault } from './hyperbeatVault'
import { readerNavOracle } from './navOracle'
import { readerNativeWnlp } from './nativeWnlp'
import { readerSaturnVault } from './saturn'
import { readerVenusHub } from './venusHub'
import { readerVesperPool } from './vesperPool'
import { readerWrenNav } from './wrenNav'
import { readerYieldBasisLt } from './yieldBasisLt'
import type { SavingsReader } from './shared'

export type { SavingsCall, SavingsOnChainState, SavingsReader } from './shared'
export { ONE_E18, toBigInt } from './shared'

/** Reader discriminant on a registry entry. Absent ⇒ `erc4626`. */
export type SavingsReaderKind =
  | 'erc4626'
  | 'erc4626-cooldown'
  | 'erc4626-idle'
  | 'erc4626-withdraw-limit'
  | 'native-wnlp'
  | 'nav-oracle'
  | 'frankencoin-savings'
  | 'hyperbeat-vault'
  | 'bitway-vault'
  | 'bitfi-bfbtc'
  | 'bitfi-vault'
  | 'yieldbasis-lt'
  | 'wren-nav'
  | 'vesper-pool'
  | 'venus-hub'
  | 'saturn-vault'

/**
 * Dispatch a registry entry to its reader.
 *
 * Most savings entries are plain ERC-4626 and set no discriminant. An
 * entry whose share token exposes a bespoke surface sets `reader` and
 * gets a case here — same pattern as
 * [../../lst/readers/index.ts](../../lst/readers/index.ts). Candidates
 * for future readers: wrapped-rebasing for srUSD, `floorPrice()` for
 * USD0++, oracle-priced RWA.
 */
export const buildReader = (entry: SavingsRegistryEntry): SavingsReader => {
  switch (entry.reader) {
    case 'native-wnlp':
      return readerNativeWnlp(entry)
    case 'nav-oracle':
      return readerNavOracle(entry)
    case 'frankencoin-savings':
      return readerFrankencoinSavings(entry)
    case 'hyperbeat-vault':
      return readerHyperbeatVault(entry)
    case 'bitway-vault':
      return readerBitwayVault(entry)
    case 'bitfi-bfbtc':
      return readerBitfiBfbtc(entry)
    case 'bitfi-vault':
      return readerBitfiVault(entry)
    case 'yieldbasis-lt':
      return readerYieldBasisLt(entry)
    case 'wren-nav':
      return readerWrenNav(entry)
    case 'vesper-pool':
      return readerVesperPool(entry)
    case 'venus-hub':
      return readerVenusHub(entry)
    case 'saturn-vault':
      return readerSaturnVault(entry)
    case 'erc4626-cooldown':
      return readerErc4626Cooldown(entry)
    case 'erc4626-idle':
      return readerErc4626Idle(entry)
    case 'erc4626-withdraw-limit':
      return readerErc4626WithdrawLimit(entry)
    case 'erc4626':
    case undefined:
      return readerErc4626(entry)
  }
}
